import { reaction } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";

import {
  getRequestsAnalyticsTabName,
  trackStructuredFilterCollectionChanges,
} from "./requestsAnalyticsTrackingUtils";

import { useStructuredSearchStore } from "@/components/StructuredSearch";
import type { Filter } from "@/components/StructuredSearch/config";
import {
  getDateRangeTrackingProperties,
  trackRequestsAnalyticsEvent,
} from "@/utils/requests-analytics-tracking";

interface StructuredSearchTrackingSnapshot {
  filters: Filter[];
  q: string;
  dateRange: {
    from: Date;
    to: Date;
  };
}

export const RequestsAnalyticsTracking = observer(
  ({ selectedTabIndex }: { selectedTabIndex: number }) => {
    const structuredSearchStore = useStructuredSearchStore();
    const hasTrackedPageViewRef = useRef(false);

    useEffect(() => {
      if (hasTrackedPageViewRef.current) {
        return;
      }

      trackRequestsAnalyticsEvent("Requests Analytics Page Viewed", {
        page: "Requests & Analytics",
        active_tab: getRequestsAnalyticsTabName(selectedTabIndex),
        path: window.location.pathname,
      });
      hasTrackedPageViewRef.current = true;
    }, [selectedTabIndex]);

    useEffect(() => {
      const disposer = reaction(
        (): StructuredSearchTrackingSnapshot => ({
          filters: structuredSearchStore.filters,
          q: structuredSearchStore.q,
          dateRange: structuredSearchStore.dateRange,
        }),
        (current, previous) => {
          if (!previous) {
            return;
          }

          if (current.q !== previous.q) {
            trackRequestsAnalyticsEvent("Requests Analytics Search Text Updated", {
              page: "Requests & Analytics",
              active_tab: getRequestsAnalyticsTabName(selectedTabIndex),
              has_search_text: current.q.trim().length > 0,
              search_text_length: current.q.trim().length,
              previous_search_text_length: previous.q.trim().length,
            });
          }

          if (
            current.dateRange.from.getTime() !== previous.dateRange.from.getTime() ||
            current.dateRange.to.getTime() !== previous.dateRange.to.getTime()
          ) {
            trackRequestsAnalyticsEvent("Requests Analytics Date Range Changed", {
              page: "Requests & Analytics",
              active_tab: getRequestsAnalyticsTabName(selectedTabIndex),
              ...getDateRangeTrackingProperties(current.dateRange),
            });
          }

          trackStructuredFilterCollectionChanges({
            currentFilters: current.filters,
            previousFilters: previous.filters,
            selectedTabIndex,
          });
        },
        { fireImmediately: false },
      );

      return () => disposer();
    }, [selectedTabIndex, structuredSearchStore]);

    return null;
  },
);
